import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Activity, Building2, CreditCard, ShieldCheck, Tag, Upload,
  Users, Power, History, BarChart3, Plus, Search, Trash2, ToggleLeft, ToggleRight,
  Database, UserCheck, BadgeCheck, Siren, Eye, LifeBuoy, ServerCog, ClipboardList, Sparkles,
  Plug, RefreshCw, FileText, Clock, AlertTriangle, Paperclip, X, LogOut, Send, UserPlus, CheckCircle2, XCircle,
} from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import api, {
  mcOverview, mcListOrgs, mcUpdateOrg, mcDisableOrg, mcEnableOrg, mcImpersonateOrg,
  mcListPlatformUsers, mcCreatePlatformUser,
  mcListPricing, mcCreatePricing, mcUpdatePricing, mcDeletePricing,
  mcListPromos, mcCreatePromo, mcDeletePromo,
  mcStripeConfig, mcStripeUpdate,
  mcEnforceMfa, mcBulkUpload, mcAudit, mcSeedDemo,
  mcGetPendingOrgs, mcApproveOrg, mcRejectOrg,
} from "@/lib/api";
import { toast } from "sonner";

const PLATFORM_ROLES = ["super_admin", "regional_admin", "financials_admin"];

const MC_VERSION = "v0.2.0";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  regional_admin: "Regional Admin",
  financials_admin: "Financials Admin",
};

const NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      { id: "overview", label: "Overview", icon: BarChart3 },
      { id: "impact", label: "Impact", icon: Sparkles },
      { id: "orgs", label: "Organizations", icon: Building2 },
    ],
  },
  {
    label: "Trust & Risk",
    items: [
      { id: "verification", label: "Verification", icon: BadgeCheck },
      { id: "trust_safety", label: "Trust & Safety", icon: Siren },
      { id: "fraud", label: "Fraud Watch", icon: Eye },
      { id: "support", label: "Help Desk", icon: LifeBuoy },
    ],
  },
  {
    label: "Billing",
    items: [
      { id: "pricing", label: "Pricing", icon: Tag },
      { id: "promos", label: "Promos", icon: Tag },
      { id: "stripe", label: "Stripe", icon: CreditCard },
    ],
  },
  {
    label: "Platform",
    items: [
      { id: "users", label: "Platform Users", icon: Users },
      { id: "mfa", label: "MFA Policy", icon: ShieldCheck },
      { id: "bulk", label: "Bulk Upload", icon: Upload },
      { id: "integrations", label: "Integrations", icon: Plug },
      { id: "ops", label: "Platform Ops", icon: ServerCog },
      { id: "compliance", label: "Compliance", icon: ClipboardList },
      { id: "audit", label: "Audit Log", icon: History },
      { id: "system", label: "System", icon: Database },
    ],
  },
];

function money(n, c = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 }).format(Number(n || 0));
}

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
      <div className="mt-2 text-3xl font-medium serif text-primary">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h3 className="font-medium text-base">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

// ============== Overview ==============
function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { mcOverview().then(setData).catch(() => setData(null)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const t = data.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Organizations" value={t.orgs} sub={`${t.active_orgs} active · ${t.paid_orgs} paid`} testId="mc-stat-orgs" />
        <StatCard label="Platform users" value={t.users} sub={`${t.mfa_adoption_pct}% with MFA`} testId="mc-stat-users" />
        <StatCard label="Expenses processed" value={t.expenses.toLocaleString()} sub={`${money(t.expense_volume_usd)} volume`} testId="mc-stat-expenses" />
        <StatCard label="Monthly recurring" value={money(t.mrr_usd)} sub={`${money(t.arr_usd)} ARR run-rate`} testId="mc-stat-mrr" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Plan distribution">
          <div className="space-y-3">
            {Object.entries(data.plan_distribution).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="capitalize">{k}</span>
                <span className="font-medium tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Recent organizations">
          <div className="divide-y divide-border/60">
            {data.recent_orgs.map((o) => (
              <div key={o.id} className="py-2.5 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{o.name}</div>
                  <div className="text-xs text-muted-foreground">{o.region || "—"} · {o.plan}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${o.is_active ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                  {o.is_active ? "active" : "disabled"}
                </span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="text-xs text-muted-foreground">
        Scope: <span className="font-medium">{data.scope}</span>{data.region ? ` · region ${data.region}` : ""}
      </div>
    </div>
  );
}

// ============== Orgs ==============
function OrgsTab() {
  const [orgs, setOrgs] = useState([]);
  const [pendingOrgs, setPendingOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionId, setActionId] = useState(null);

  const reload = async (searchTerm) => {
    try {
      const [r, pending] = await Promise.all([
        mcListOrgs(searchTerm ? { q: searchTerm } : {}),
        mcGetPendingOrgs().catch(() => ({ pending_orgs: [] })),
      ]);
      setOrgs(r.orgs);
      setPendingOrgs(pending.pending_orgs || []);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, pending] = await Promise.all([
          mcListOrgs({}),
          mcGetPendingOrgs().catch(() => ({ pending_orgs: [] })),
        ]);
        if (!cancelled) {
          setOrgs(r.orgs);
          setPendingOrgs(pending.pending_orgs || []);
        }
      } catch (e) {
        // silent catch
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleApprove = async (pOrg) => {
    setActionId(pOrg.id);
    try {
      await mcApproveOrg(pOrg.id);
      toast.success(`Approved ${pOrg.name}! Activation email sent to administrator.`);
      await reload(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve organization");
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (pOrg) => {
    const reason = window.prompt(`Reason for rejecting "${pOrg.name}"? (Optional):`);
    setActionId(pOrg.id);
    try {
      await mcRejectOrg(pOrg.id, reason || undefined);
      toast.success(`Organization ${pOrg.name} rejected.`);
      await reload(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reject organization");
    } finally {
      setActionId(null);
    }
  };

  const toggle = async (o) => {
    try {
      if (o.is_active) await mcDisableOrg(o.id); else await mcEnableOrg(o.id);
      toast.success(`Org ${o.is_active ? "disabled" : "enabled"}`);
      reload(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setPlan = async (o, plan) => {
    try {
      await mcUpdateOrg(o.id, { plan });
      toast.success(`Plan updated to ${plan}`);
      reload(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const customerMode = async (o) => {
    try {
      const r = await mcImpersonateOrg(o.id);
      const url = `/customer-mode/handoff?token=${encodeURIComponent(r.access_token)}&org=${encodeURIComponent(o.name)}`;
      window.open(url, "_blank", "noopener");
      toast.success(`Opening customer mode for ${o.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      {pendingOrgs.length > 0 && (
        <Section title={`Pending Registration Approvals (${pendingOrgs.length})`}>
          <div className="divide-y divide-border border border-amber-500/30 rounded-xl bg-amber-500/5 overflow-hidden">
            {pendingOrgs.map((po) => (
              <div key={po.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-base">{po.name}</span>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                      Pending Super Admin Approval
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Admin: <strong className="text-foreground">{po.admin_name}</strong> ({po.admin_email})</span>
                    <span>Requested: <span className="font-mono">{new Date(po.created_at).toLocaleDateString()}</span></span>
                    <span>Plan: <span className="capitalize font-medium">{po.plan}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleApprove(po)}
                    disabled={actionId === po.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    <CheckCircle2 size={14} /> Approve & Activate
                  </button>
                  <button
                    onClick={() => handleReject(po)}
                    disabled={actionId === po.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600/10 hover:bg-rose-600/20 text-rose-600 dark:text-rose-400 border border-rose-500/20 text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title={`Organizations (${orgs.length})`} action={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name/slug"
              data-testid="mc-orgs-search"
              className="pl-7 pr-3 py-1.5 text-sm rounded-md border border-border bg-background" />
          </div>
          <button onClick={async () => { setBusy(true); await reload(q); }} disabled={busy} className="text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground">Search</button>
        </div>
      }>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left py-2 font-medium">Name</th>
              <th className="text-left py-2 font-medium">Region</th>
              <th className="text-left py-2 font-medium">Plan</th>
              <th className="text-right py-2 font-medium">Users</th>
              <th className="text-right py-2 font-medium">Expenses</th>
              <th className="text-left py-2 font-medium">Status</th>
              <th className="text-right py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className="border-b border-border/40" data-testid={`mc-org-row-${o.slug}`}>
                <td className="py-2.5">
                  <div className="font-medium">{o.name}</div>
                  <div className="text-xs text-muted-foreground">{o.slug}</div>
                </td>
                <td className="py-2.5 text-muted-foreground">{o.region || "—"}</td>
                <td className="py-2.5">
                  <select value={o.plan} onChange={(e) => setPlan(o, e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1">
                    <option value="community">Community</option>
                    <option value="growth">Growth</option>
                    <option value="impact">Impact</option>
                  </select>
                </td>
                <td className="py-2.5 text-right tabular-nums">{o.user_count}</td>
                <td className="py-2.5 text-right tabular-nums">{o.expense_count}</td>
                <td className="py-2.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${o.is_active ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                    {o.is_active ? "active" : "disabled"}
                  </span>
                </td>
                <td className="py-2.5 text-right">
                  <div className="inline-flex items-center gap-1 justify-end">
                    <button onClick={() => customerMode(o)} data-testid={`mc-org-impersonate-${o.slug}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-secondary/80 text-secondary-foreground hover:bg-secondary"
                      title="Open in customer mode (impersonate org admin)">
                      <UserCheck size={14} /> Customer mode
                    </button>
                    <button onClick={() => toggle(o)} data-testid={`mc-org-toggle-${o.slug}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border hover:bg-accent">
                      {o.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      {o.is_active ? "Disable" : "Enable"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!orgs.length && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No orgs</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
    </div>
  );
}

// ============== Platform users ==============
function PlatformUsersTab() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "regional_admin", region: "" });
  const load = () => mcListPlatformUsers().then(setUsers);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await mcCreatePlatformUser({ ...form, region: form.region || null });
      toast.success("Platform user created");
      setForm({ email: "", password: "", full_name: "", role: "regional_admin", region: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Section title={`Platform users (${users.length})`}>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Role</th>
                <th className="text-left py-2">Region</th>
                <th className="text-left py-2">MFA</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border/40">
                  <td className="py-2.5">
                    <div className="font-medium">{u.email}</div>
                    <div className="text-xs text-muted-foreground">{u.full_name}</div>
                  </td>
                  <td className="py-2.5"><span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{u.role}</span></td>
                  <td className="py-2.5 text-muted-foreground">{u.region || "—"}</td>
                  <td className="py-2.5">{u.mfa_enabled ? "✓" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
      <Section title="Invite platform user">
        <div className="space-y-3 text-sm">
          <input data-testid="mc-pu-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <input data-testid="mc-pu-name" placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <input data-testid="mc-pu-password" placeholder="Initial password (8+)" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <select data-testid="mc-pu-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background">
            <option value="regional_admin">Regional Admin</option>
            <option value="financials_admin">Financials Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <input data-testid="mc-pu-region" placeholder="Region (optional)" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <button onClick={create} data-testid="mc-pu-create"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md">
            <Plus size={14} /> Create
          </button>
        </div>
      </Section>
    </div>
  );
}

// ============== Pricing ==============
function PricingTab() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", monthly_price: 0, annual_price: 0, tagline: "", features: "" });
  const load = () => mcListPricing().then(setTiers);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await mcCreatePricing({
        ...form,
        monthly_price: Number(form.monthly_price),
        annual_price: Number(form.annual_price) || null,
        features: form.features.split("\n").filter(Boolean),
      });
      toast.success("Tier created");
      setForm({ code: "", name: "", monthly_price: 0, annual_price: 0, tagline: "", features: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const togglePrice = async (t, field, value) => {
    try { await mcUpdatePricing(t.code, { [field]: value }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {tiers.map((t) => (
          <div key={t.code} className="rounded-xl border border-border bg-card p-5" data-testid={`mc-tier-${t.code}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{t.name}</h4>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-accent text-muted-foreground">{t.code}</span>
                  {!t.is_active && <span className="text-[11px] px-2 py-0.5 rounded bg-rose-100 text-rose-800">inactive</span>}
                </div>
                {t.tagline && <p className="text-sm text-muted-foreground mt-1">{t.tagline}</p>}
                <ul className="mt-3 space-y-1 text-sm">
                  {(t.features || []).map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
              </div>
              <div className="text-right space-y-2 min-w-[150px]">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Monthly</label>
                  <input type="number" defaultValue={t.monthly_price} onBlur={(e) => togglePrice(t, "monthly_price", Number(e.target.value))}
                    data-testid={`mc-tier-${t.code}-monthly`}
                    className="w-24 text-right border border-border rounded px-2 py-1 bg-background text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Annual</label>
                  <input type="number" defaultValue={t.annual_price || 0} onBlur={(e) => togglePrice(t, "annual_price", Number(e.target.value))}
                    className="w-24 text-right border border-border rounded px-2 py-1 bg-background text-sm" />
                </div>
                <button onClick={() => togglePrice(t, "is_active", !t.is_active)}
                  className="text-xs px-2 py-1 rounded border border-border">
                  {t.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Section title="New pricing tier">
        <div className="space-y-3 text-sm">
          <input placeholder="Code (e.g. enterprise)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
            data-testid="mc-tier-new-code"
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <input placeholder="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <div className="grid grid-cols-2 gap-2">
            <input type="number" placeholder="Monthly $" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
              className="px-3 py-2 rounded-md border border-border bg-background" />
            <input type="number" placeholder="Annual $" value={form.annual_price} onChange={(e) => setForm({ ...form, annual_price: e.target.value })}
              className="px-3 py-2 rounded-md border border-border bg-background" />
          </div>
          <input placeholder="Tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <textarea placeholder="Features (one per line)" rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <button onClick={create} data-testid="mc-tier-create"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md">
            <Plus size={14} /> Create tier
          </button>
        </div>
      </Section>
    </div>
  );
}

// ============== Promos ==============
function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", description: "", discount_type: "percent", discount_value: 10, max_uses: "", applies_to: "" });
  const load = () => mcListPromos().then(setPromos);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await mcCreatePromo({
        ...form,
        code: form.code.toUpperCase(),
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        applies_to: form.applies_to ? form.applies_to.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Promo created");
      setForm({ code: "", description: "", discount_type: "percent", discount_value: 10, max_uses: "", applies_to: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete promo ${code}?`)) return;
    await mcDeletePromo(code);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Section title={`Active promos (${promos.length})`}>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left py-2">Code</th>
                <th className="text-left py-2">Discount</th>
                <th className="text-left py-2">Applies to</th>
                <th className="text-right py-2">Used</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => (
                <tr key={p.code} className="border-b border-border/40" data-testid={`mc-promo-${p.code}`}>
                  <td className="py-2.5">
                    <div className="font-mono font-medium">{p.code}</div>
                    <div className="text-xs text-muted-foreground">{p.description}</div>
                  </td>
                  <td className="py-2.5">{p.discount_type === "percent" ? `${p.discount_value}%` : `$${p.discount_value}`}</td>
                  <td className="py-2.5 text-xs text-muted-foreground">{(p.applies_to || []).join(", ") || "All"}</td>
                  <td className="py-2.5 text-right tabular-nums">{p.used_count}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                  <td className="py-2.5 text-right">
                    <button onClick={() => del(p.code)} className="text-rose-700 hover:bg-rose-50 rounded p-1">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
      <Section title="New promo code">
        <div className="space-y-3 text-sm">
          <input placeholder="CODE (uppercase)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            data-testid="mc-promo-new-code"
            className="w-full px-3 py-2 rounded-md border border-border bg-background font-mono" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
              className="px-3 py-2 rounded-md border border-border bg-background">
              <option value="percent">% off</option>
              <option value="fixed">$ off</option>
            </select>
            <input type="number" placeholder="Value" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              className="px-3 py-2 rounded-md border border-border bg-background" />
          </div>
          <input placeholder="Applies to (growth,impact)" value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <input placeholder="Max uses (blank = unlimited)" type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-border bg-background" />
          <button onClick={create} data-testid="mc-promo-create"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md">
            <Plus size={14} /> Create promo
          </button>
        </div>
      </Section>
    </div>
  );
}

// ============== Stripe ==============
function StripeTab() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({ publishable_key: "", is_active: true });
  useEffect(() => {
    mcStripeConfig().then((c) => { setCfg(c); setDraft({ publishable_key: c.publishable_key || "", is_active: c.is_active }); });
  }, []);

  const save = async () => {
    try {
      await mcStripeUpdate(draft);
      const c = await mcStripeConfig();
      setCfg(c);
      toast.success("Stripe config updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title="Stripe gateway">
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between p-3 rounded-md bg-accent/50">
            <div>
              <div className="font-medium">Secret key (server-side)</div>
              <div className="text-xs text-muted-foreground">Set via STRIPE_API_KEY in backend env</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.has_secret_key_env ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              {cfg.has_secret_key_env ? "configured" : "missing"}
            </span>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Publishable key</label>
            <input value={draft.publishable_key} onChange={(e) => setDraft({ ...draft, publishable_key: e.target.value })}
              data-testid="mc-stripe-pub-key" placeholder="pk_live_… or pk_test_…"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background font-mono text-xs" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
            <span>Gateway active</span>
          </label>
          <button onClick={save} data-testid="mc-stripe-save"
            className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-md">Save</button>
        </div>
      </Section>
      <Section title="Notes">
        <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
          <li>The platform uses Stripe via Emergent&apos;s proxy — the server-side <code>STRIPE_API_KEY</code> is read from environment.</li>
          <li>Set <em>Gateway active</em> to <strong>off</strong> to suspend new checkouts platform-wide.</li>
          <li>Connect customers&apos; subscriptions via the Billing tab in each org.</li>
        </ul>
      </Section>
    </div>
  );
}

// ============== MFA Policy ==============
function MfaTab() {
  const [policy, setPolicy] = useState("required");
  const [region, setRegion] = useState("");
  const [last, setLast] = useState(null);

  const apply = async () => {
    try {
      const r = await mcEnforceMfa(policy, region || null);
      setLast(r);
      toast.success(`Updated ${r.orgs_updated} orgs to ${r.policy}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title="Enforce MFA across orgs">
        <div className="space-y-4 text-sm">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Policy</label>
            <select value={policy} onChange={(e) => setPolicy(e.target.value)}
              data-testid="mc-mfa-policy"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background">
              <option value="disabled">Disabled — users cannot enable MFA</option>
              <option value="optional">Optional — users may enable MFA</option>
              <option value="required">Required — all users must enable MFA at next login</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Region (blank = all)</label>
            <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. North America"
              data-testid="mc-mfa-region"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background" />
          </div>
          <button onClick={apply} data-testid="mc-mfa-apply"
            className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-md">Apply policy</button>
          {last && (
            <div className="text-xs p-3 rounded bg-accent/50">
              Last action: {last.orgs_updated} orgs set to <strong>{last.policy}</strong>
            </div>
          )}
        </div>
      </Section>
      <Section title="How it works">
        <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
          <li><strong>Required</strong> orgs force every user to set up TOTP/passkey before they can access the dashboard.</li>
          <li><strong>Optional</strong> lets users self-serve under <em>Security</em>.</li>
          <li><strong>Disabled</strong> hides MFA UI (use only for sandbox tenants).</li>
        </ul>
      </Section>
    </div>
  );
}

// ============== Bulk Upload ==============
function BulkUploadTab() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [entity, setEntity] = useState("members");
  const [csv, setCsv] = useState("email,full_name,role,password\njane@demo.org,Jane Doe,volunteer,Pass123!");
  const [result, setResult] = useState(null);

  useEffect(() => { mcListOrgs({}).then((r) => { setOrgs(r.orgs); if (r.orgs[0]) setOrgId(r.orgs[0].id); }); }, []);

  const parseCsv = (text) => {
    const lines = text.trim().split("\n").filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const cells = line.split(",").map((c) => c.trim());
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    });
  };

  const upload = async () => {
    try {
      const rows = parseCsv(csv);
      if (!rows.length) { toast.error("No rows parsed"); return; }
      const r = await mcBulkUpload({ entity, organization_id: orgId, rows });
      setResult(r);
      toast.success(`${r.inserted}/${r.total_rows} rows inserted`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const templates = {
    members: "email,full_name,role,password\njane@demo.org,Jane Doe,volunteer,Pass123!\nbob@demo.org,Bob Smith,field_operator,Pass123!",
    programs: "name,budget_amount,description\nOutreach Program,5000,Community outreach",
    expenses: "vendor,amount,category,spent_on,notes\nCostco,142.50,Supplies,2026-02-01,Pantry restock",
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title="Bulk upload">
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Target org</label>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                data-testid="mc-bulk-org"
                className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background">
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Entity</label>
              <select value={entity} onChange={(e) => { setEntity(e.target.value); setCsv(templates[e.target.value]); }}
                data-testid="mc-bulk-entity"
                className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background">
                <option value="members">Members</option>
                <option value="programs">Programs</option>
                <option value="expenses">Expenses</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">CSV (with header row)</label>
            <textarea rows={8} value={csv} onChange={(e) => setCsv(e.target.value)}
              data-testid="mc-bulk-csv"
              className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background font-mono text-xs" />
          </div>
          <button onClick={upload} data-testid="mc-bulk-upload"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md">
            <Upload size={14} /> Upload
          </button>
        </div>
      </Section>
      <Section title="Result">
        {result ? (
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded bg-emerald-50 text-emerald-800">
              <strong>{result.inserted}</strong> / {result.total_rows} rows inserted
            </div>
            {result.errors?.length > 0 && (
              <div className="p-3 rounded bg-rose-50 text-rose-800 text-xs">
                <div className="font-medium mb-1">Errors:</div>
                <ul className="space-y-1">
                  {result.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Run an upload to see the result here.</p>
        )}
      </Section>
    </div>
  );
}

// ============== Audit ==============
function AuditTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { mcAudit(200).then(setRows); }, []);
  return (
    <Section title={`Recent audit events (${rows.length})`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left py-2">When</th>
              <th className="text-left py-2">Actor</th>
              <th className="text-left py-2">Action</th>
              <th className="text-left py-2">Target</th>
              <th className="text-left py-2">Meta</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/40">
                <td className="py-2 text-xs text-muted-foreground tabular-nums">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-2"><div className="font-medium text-xs">{r.actor_email}</div><div className="text-[11px] text-muted-foreground">{r.actor_role}</div></td>
                <td className="py-2 font-mono text-xs">{r.action}</td>
                <td className="py-2 font-mono text-xs">{r.target}</td>
                <td className="py-2 text-xs text-muted-foreground"><code>{JSON.stringify(r.meta || {})}</code></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No events</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ============== System / Demo data ==============
function SystemTab() {
  const [busy, setBusy] = useState(false);
  const reSeed = async (force) => {
    setBusy(true);
    try {
      const r = await mcSeedDemo(force);
      toast.success(`Seed: ${r.status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Section title="System">
      <div className="space-y-4 text-sm">
        <div>
          <h4 className="font-medium">Demo data</h4>
          <p className="text-muted-foreground text-xs mt-1">5 demo orgs, 50+ users, 200+ expenses with 12 months of history.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={() => reSeed(false)} disabled={busy} data-testid="mc-seed"
              className="text-sm px-3 py-2 rounded-md bg-primary text-primary-foreground">Seed (if missing)</button>
            <button onClick={() => reSeed(true)} disabled={busy} data-testid="mc-reseed"
              className="text-sm px-3 py-2 rounded-md border border-border">Re-seed (wipe demo data)</button>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ============== Alerts Center (used on Overview) ==============
function AlertsCenter() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/api/mission-control/alerts").then((r) => setA(r.data)).catch(() => setA({})); }, []);
  if (!a) return <div className="text-xs text-muted-foreground">Loading alerts…</div>;
  const items = [
    { label: "Approval backlog (>7d)", value: a.backlog_approvals, tone: a.backlog_approvals > 0 ? "warning" : "ok" },
    { label: "Subscriptions expiring (<14d)", value: a.subscription_expiring_soon, tone: a.subscription_expiring_soon > 5 ? "warning" : "ok" },
    { label: "OCR failures", value: a.ocr_failures, tone: a.ocr_failures > 0 ? "warning" : "ok" },
    { label: "Repeated rejections", value: a.repeated_rejections, tone: a.repeated_rejections > 0 ? "warning" : "ok" },
    { label: "Suspended orgs", value: a.suspended_orgs, tone: a.suspended_orgs > 0 ? "danger" : "ok" },
  ];
  return (
    <Section title="Alerts Center">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {items.map((it) => (
          <div key={it.label} className={`rounded-lg border p-3 ${
            it.tone === "danger" ? "border-rose-500/40 bg-rose-500/10" :
            it.tone === "warning" ? "border-amber-500/40 bg-amber-500/10" :
            "border-emerald-500/30 bg-emerald-500/5"
          }`}>
            <div className="text-2xl serif">{it.value ?? 0}</div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-1">{it.label}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ============== Verification + Trust & Safety + Support (shared Cases UI) ==============
function slaInfo(c) {
  if (!c?.sla_due_at) return null;
  const due = new Date(c.sla_due_at);
  const now = new Date();
  const ms = due - now;
  const isResolved = ["resolved", "closed"].includes(c.status);
  const overdue = !isResolved && ms < 0;
  const absMin = Math.abs(Math.round(ms / 60000));
  let label;
  if (absMin < 60) label = `${absMin}m`;
  else if (absMin < 60 * 24) label = `${Math.round(absMin / 60)}h`;
  else label = `${Math.round(absMin / (60 * 24))}d`;
  return { label, overdue, isResolved, dueIso: due.toISOString() };
}

function CasesTab({ kind, title, subtitle, severityDefault = "medium" }) {
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", organization_id: "", severity: severityDefault });
  const [activeCase, setActiveCase] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("ein_letter");

  const load = () => api.get("/api/mission-control/cases", { params: { kind } }).then((r) => setRows(r.data));
  useEffect(() => { load(); mcListOrgs({}).then((r) => setOrgs(r.orgs)); }, []); // eslint-disable-line

  const create = async () => {
    try {
      await api.post("/api/mission-control/cases", { ...form, kind, organization_id: form.organization_id || null });
      toast.success("Case opened");
      setOpen(false); setForm({ title: "", description: "", organization_id: "", severity: severityDefault });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (c, status) => {
    try { await api.patch(`/api/mission-control/cases/${c.id}`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openCase = async (c) => {
    setActiveCase(c);
    if (kind === "verification") {
      try {
        const r = await api.get(`/api/mission-control/cases/${c.id}/documents`);
        setDocs(r.data);
      } catch { setDocs([]); }
    } else {
      setDocs([]);
    }
  };

  const closeCase = () => { setActiveCase(null); setDocs([]); };

  const uploadDoc = async (file) => {
    if (!file || !activeCase) return;
    if (file.size > 600 * 1024) { toast.error("File must be ≤ 600 KB"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.post(`/api/mission-control/cases/${activeCase.id}/documents`, {
            case_id: activeCase.id,
            doc_type: docType,
            file_name: file.name,
            file_data_url: reader.result,
          });
          toast.success("Document uploaded");
          const r = await api.get(`/api/mission-control/cases/${activeCase.id}/documents`);
          setDocs(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) { setUploading(false); toast.error("Read failed"); }
  };

  return (
    <div className="space-y-4">
      <Section title={`${title} (${rows.length})`} action={
        <button onClick={() => setOpen((v) => !v)} data-testid={`mc-cases-${kind}-new`}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
          <Plus size={14} /> New case
        </button>
      }>
        {open && (
          <div className="border border-border rounded-lg p-4 mb-4 bg-background/50">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 rounded border border-border bg-background col-span-2" />
              <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} className="px-3 py-2 rounded border border-border bg-background">
                <option value="">-- Org (optional) --</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="px-3 py-2 rounded border border-border bg-background">
                {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-3 py-2 rounded border border-border bg-background col-span-2" />
            </div>
            <button onClick={create} disabled={!form.title} className="mt-3 px-3 py-2 rounded bg-primary text-primary-foreground text-sm">Open case</button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>
        {rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">No {title.toLowerCase()} cases yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left py-2">Title</th>
                <th className="text-left py-2">Severity</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">SLA</th>
                <th className="text-left py-2">Opened</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const sla = slaInfo(c);
                return (
                  <tr key={c.id} className="border-b border-border/40 hover:bg-background/40 cursor-pointer" data-testid={`mc-case-${c.id}`} onClick={() => openCase(c)}>
                    <td className="py-2.5"><div className="font-medium">{c.title}</div><div className="text-xs text-muted-foreground">{c.description?.slice(0, 80)}</div></td>
                    <td className="py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded uppercase ${
                      c.severity === "critical" ? "bg-rose-500/20 text-rose-400" :
                      c.severity === "high" ? "bg-amber-500/20 text-amber-400" :
                      "bg-accent text-muted-foreground"
                    }`}>{c.severity}</span></td>
                    <td className="py-2.5">{c.status}</td>
                    <td className="py-2.5">
                      {sla ? (
                        <span data-testid={`mc-sla-${c.id}`} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded uppercase ${
                          sla.isResolved ? "bg-emerald-500/15 text-emerald-400" :
                          sla.overdue ? "bg-rose-500/20 text-rose-400" :
                          "bg-sky-500/15 text-sky-400"
                        }`} title={`Due ${sla.dueIso}`}>
                          {sla.overdue ? <AlertTriangle size={10} /> : <Clock size={10} />}
                          {sla.isResolved ? "met" : sla.overdue ? `+${sla.label} late` : sla.label}
                        </span>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2.5 text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <select defaultValue={c.status} onChange={(e) => setStatus(c, e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1">
                        {["open", "investigating", "resolved", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {activeCase && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={closeCase}>
          <div className="bg-card border border-border rounded-t-xl md:rounded-xl w-full md:w-[640px] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mc-case-detail">
            <div className="px-5 py-4 border-b border-border flex items-start justify-between">
              <div>
                <h3 className="font-medium text-base">{activeCase.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{activeCase.kind} · {activeCase.severity} · opened {new Date(activeCase.created_at).toLocaleString()}</p>
              </div>
              <button onClick={closeCase} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-5">
              {activeCase.description && (
                <p className="text-sm text-foreground/90 whitespace-pre-wrap">{activeCase.description}</p>
              )}
              {kind === "verification" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium flex items-center gap-2"><FileText size={14} /> Documents ({docs.length})</h4>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <select value={docType} onChange={(e) => setDocType(e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1.5">
                      <option value="ein_letter">EIN letter</option>
                      <option value="501c3_letter">501(c)(3) letter</option>
                      <option value="registration">Registration</option>
                      <option value="bank_letter">Bank verification</option>
                      <option value="id_proof">ID proof</option>
                      <option value="other">Other</option>
                    </select>
                    <label className="text-xs px-3 py-1.5 rounded border border-border bg-background/50 cursor-pointer inline-flex items-center gap-1.5 hover:bg-background">
                      <Paperclip size={12} /> {uploading ? "Uploading…" : "Upload document"}
                      <input data-testid="mc-doc-upload" type="file" className="hidden" disabled={uploading} accept="image/*,application/pdf" onChange={(e) => uploadDoc(e.target.files?.[0])} />
                    </label>
                  </div>
                  {docs.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border/60 rounded">No documents uploaded yet.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {docs.map((d) => (
                        <li key={d.id} className="flex items-center justify-between text-sm border border-border/40 rounded px-3 py-2 bg-background/40">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={14} className="shrink-0" />
                            <div className="min-w-0">
                              <div className="font-medium truncate">{d.file_name}</div>
                              <div className="text-[11px] text-muted-foreground">{d.doc_type} · {new Date(d.uploaded_at).toLocaleString()}</div>
                            </div>
                          </div>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 uppercase">{d.review_status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {activeCase.events?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Timeline</h4>
                  <ol className="space-y-1.5 text-xs">
                    {activeCase.events.slice().reverse().map((ev, i) => (
                      <li key={i} className="border-l-2 border-border pl-3 py-1">
                        <div className="text-foreground">{ev.note || ev.action || "event"}</div>
                        <div className="text-muted-foreground">{ev.actor_email} · {new Date(ev.at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== Fraud Watch ==============
function FraudTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/api/mission-control/fraud-watch").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Scanning…</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Section title={`Duplicate receipts (${data.duplicate_receipts.length})`}>
        {data.duplicate_receipts.length === 0 ? <p className="text-sm text-muted-foreground">No duplicates detected.</p> : (
          <ul className="space-y-2 text-sm">
            {data.duplicate_receipts.map((d, i) => (
              <li key={i} className="border-b border-border/40 pb-2">
                <div className="flex justify-between"><span className="font-medium">{d.vendor}</span><span className="tabular-nums">${d.amount}</span></div>
                <div className="text-xs text-muted-foreground">{d.occurrences} matches · org {d.organization_id?.slice(0, 8)}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title={`Unusual spending — ${data.unusual_spending.length} orgs`}>
        {data.unusual_spending.length === 0 ? <p className="text-sm text-muted-foreground">No unusual patterns.</p> : (
          <ul className="space-y-2 text-sm">
            {data.unusual_spending.map((u) => (
              <li key={u.organization_id} className="border-b border-border/40 pb-2">
                <div className="flex justify-between"><span className="font-medium">{u.organization_name}</span><span className="text-xs">{u.outlier_count} outliers</span></div>
                <div className="text-xs text-muted-foreground">median ${u.median} · threshold ${u.threshold}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ============== Platform Ops ==============
function OpsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/api/mission-control/ops/health").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Pinging services…</div>;
  return (
    <div className="space-y-6">
      <Section title="System health">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="p-4 rounded-lg border border-border bg-background/50">
            <div className="text-xs uppercase text-muted-foreground">MongoDB</div>
            <div className={`mt-1 text-2xl serif ${data.database.ok ? "text-emerald-400" : "text-rose-400"}`}>{data.database.ok ? "OK" : "DOWN"}</div>
            <div className="text-xs text-muted-foreground mt-1">{data.database.latency_ms} ms ping</div>
          </div>
          <div className="p-4 rounded-lg border border-border bg-background/50">
            <div className="text-xs uppercase text-muted-foreground">API</div>
            <div className="mt-1 text-2xl serif text-emerald-400">OK</div>
            <div className="text-xs text-muted-foreground mt-1">/api/health 200</div>
          </div>
          <div className="p-4 rounded-lg border border-border bg-background/50">
            <div className="text-xs uppercase text-muted-foreground">Last check</div>
            <div className="mt-1 text-sm">{new Date(data.checked_at).toLocaleTimeString()}</div>
          </div>
        </div>
      </Section>
      <Section title="Collection sizes">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {Object.entries(data.collections).map(([k, v]) => (
            <div key={k} className="flex justify-between p-2 rounded bg-background/40"><span className="font-mono text-xs">{k}</span><span className="tabular-nums">{v.toLocaleString()}</span></div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ============== Compliance ==============
function ComplianceTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/api/mission-control/compliance/posture").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading posture…</div>;
  return (
    <div className="space-y-6">
      <Section title="Coverage">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <StatCard label="MFA coverage" value={`${data.mfa_coverage_pct}%`} />
          <StatCard label="Orgs requiring MFA" value={`${data.orgs_requiring_mfa_pct}%`} />
          <StatCard label="Audit events" value={data.audit_events_logged.toLocaleString()} />
        </div>
      </Section>
      <Section title="Controls">
        <div className="space-y-2 text-sm">
          {data.controls.map((c) => (
            <div key={c.id} className="flex items-start justify-between border-b border-border/40 py-2.5">
              <div>
                <div className="font-medium">{c.name} <span className="text-[10px] font-mono text-muted-foreground ml-1">{c.id}</span></div>
                <div className="text-xs text-muted-foreground">{c.evidence}</div>
              </div>
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${
                c.status === "passing" ? "bg-emerald-500/15 text-emerald-400" :
                c.status === "in_progress" ? "bg-amber-500/15 text-amber-400" :
                "bg-rose-500/15 text-rose-400"
              }`}>{c.status.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ============== Impact ==============
function ImpactTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/api/mission-control/impact").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading impact…</div>;
  const regions = data.regions_represented || [];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Orgs served" value={data.organizations_served || 0} />
        <StatCard label="Donations tracked" value={`$${(data.donations_tracked_usd || 0).toLocaleString()}`} sub={`${data.donation_count || 0} gifts`} />
        <StatCard label="Programs funded" value={data.programs_funded || 0} />
        <StatCard label="Volunteers" value={data.volunteers_supported || 0} />
        <StatCard label="Regions" value={regions.length || data.regions_active || 0} sub={regions.length > 0 ? regions.join(" · ") : "N/A"} />
      </div>
    </div>
  );
}

// ============== Fraud Rules ==============
function FraudRulesTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "duplicate", threshold: 2, severity: "medium", description: "" });

  const load = () => api.get("/api/mission-control/fraud-rules").then((r) => setRows(r.data));
  useEffect(() => {
    let cancelled = false;
    api.get("/api/mission-control/fraud-rules").then((r) => { if (!cancelled) setRows(r.data); });
    return () => { cancelled = true; };
  }, []);

  const create = async () => {
    try {
      await api.post("/api/mission-control/fraud-rules", { ...form, threshold: Number(form.threshold) });
      toast.success("Rule created");
      setOpen(false); setForm({ name: "", kind: "duplicate", threshold: 2, severity: "medium", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (rule) => {
    try { await api.patch(`/api/mission-control/fraud-rules/${rule.id}?is_active=${!rule.is_active}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    await api.delete(`/api/mission-control/fraud-rules/${rule.id}`);
    load();
  };

  return (
    <Section title={`Fraud rules (${rows.length})`} action={
      <button onClick={() => setOpen((v) => !v)} data-testid="mc-fraud-rules-new"
        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
        <Plus size={14} /> New rule
      </button>
    }>
      {open && (
        <div className="border border-border rounded-lg p-4 mb-4 bg-background/50 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <input placeholder="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="mc-fraud-rule-name" className="px-3 py-2 rounded border border-border bg-background col-span-2" />
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="mc-fraud-rule-kind" className="px-3 py-2 rounded border border-border bg-background">
              <option value="duplicate">Duplicate receipt</option>
              <option value="amount_zscore">Amount z-score outlier</option>
              <option value="vendor_frequency">Vendor frequency</option>
              <option value="rejection_streak">Rejection streak</option>
              <option value="custom">Custom</option>
            </select>
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
              className="px-3 py-2 rounded border border-border bg-background">
              {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="number" placeholder="Threshold" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })}
              className="px-3 py-2 rounded border border-border bg-background" />
            <textarea rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="px-3 py-2 rounded border border-border bg-background col-span-2" />
          </div>
          <button onClick={create} disabled={!form.name} data-testid="mc-fraud-rule-create"
            className="px-3 py-2 rounded bg-primary text-primary-foreground text-sm">Create rule</button>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">No fraud rules yet. Add one above.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left py-2">Name</th>
              <th className="text-left py-2">Kind</th>
              <th className="text-left py-2">Threshold</th>
              <th className="text-left py-2">Severity</th>
              <th className="text-right py-2">Hits</th>
              <th className="text-left py-2">Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/40" data-testid={`mc-fraud-rule-${r.id}`}>
                <td className="py-2.5"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.description}</div></td>
                <td className="py-2.5 text-xs font-mono">{r.kind}</td>
                <td className="py-2.5 tabular-nums">{r.threshold}</td>
                <td className="py-2.5"><span className={`text-[10px] uppercase px-2 py-0.5 rounded ${
                  r.severity === "critical" ? "bg-rose-500/20 text-rose-400" :
                  r.severity === "high" ? "bg-amber-500/20 text-amber-400" :
                  "bg-accent text-muted-foreground"
                }`}>{r.severity}</span></td>
                <td className="py-2.5 text-right tabular-nums">{r.hit_count || 0}</td>
                <td className="py-2.5">
                  <button onClick={() => toggle(r)} className="text-xs px-2 py-1 rounded border border-border">
                    {r.is_active ? "ON" : "OFF"}
                  </button>
                </td>
                <td className="py-2.5 text-right">
                  <button onClick={() => del(r)} className="text-rose-400 hover:bg-rose-500/10 rounded p-1"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// ============== Integrations (QBO + Zoho Books) ==============
function IntegrationsTab() {
  const [items, setItems] = useState([]);
  const [activeProvider, setActiveProvider] = useState(null);
  const [credForm, setCredForm] = useState({});
  const [syncing, setSyncing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/api/mission-control/integrations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const activeMeta = items.find((i) => i.provider === activeProvider);

  const openConnect = (it) => {
    setActiveProvider(it.provider);
    const initial = {};
    it.required_fields.forEach((f) => { initial[f.key] = ""; });
    setCredForm(initial);
  };

  const submitConnect = async () => {
    try {
      await api.post(`/api/mission-control/integrations/${activeProvider}/connect`, { fields: credForm });
      toast.success(`${activeMeta?.name} connected`);
      setActiveProvider(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connect failed"); }
  };

  const disconnect = async (it) => {
    if (!window.confirm(`Disconnect ${it.name}? Stored credentials will be cleared.`)) return;
    try {
      await api.post(`/api/mission-control/integrations/${it.provider}/disconnect`);
      toast.success("Disconnected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sync = async (it) => {
    setSyncing(it.provider);
    try {
      const r = await api.post(`/api/mission-control/integrations/${it.provider}/sync`);
      toast.success(`Synced · ${Object.values(r.data.totals).reduce((a, b) => a + b, 0)} records`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setSyncing(null);
  };

  const openHistory = async (it) => {
    setHistoryOpen(it.provider);
    try {
      const r = await api.get(`/api/mission-control/integrations/${it.provider}/sync-history`);
      setHistory(r.data);
    } catch { setHistory([]); }
  };

  return (
    <div className="space-y-6">
      <Section title="Accounting integrations" >
        <p className="text-xs text-muted-foreground mb-4">
          Push expenses and pull chart-of-accounts into your accounting suite. Sync runs are
          simulated until production credentials are activated — UI surfaces wire through
          unchanged once live OAuth is enabled.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => {
            const connected = it.status === "connected";
            return (
              <div key={it.provider} data-testid={`mc-integration-${it.provider}`} className="rounded-xl border border-border bg-background/40 p-5 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Plug size={16} className="text-primary" />
                      <h4 className="font-medium">{it.name}</h4>
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{it.vendor} · {it.category}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase ${
                    connected ? "bg-emerald-500/15 text-emerald-400" :
                    it.status === "disconnected" ? "bg-amber-500/15 text-amber-400" :
                    "bg-accent text-muted-foreground"
                  }`}>{it.status.replace("_", " ")}</span>
                </div>
                <p className="text-xs text-muted-foreground">{it.description}</p>

                {connected && (
                  <div className="text-xs space-y-1 border border-border/50 rounded-md p-3 bg-background/60">
                    {Object.entries(it.fields).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3">
                        <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="font-mono truncate max-w-[60%] text-right">{v || "—"}</span>
                      </div>
                    ))}
                  </div>
                )}

                {connected && it.last_sync_at && (
                  <div className="text-xs text-muted-foreground border-t border-border/40 pt-2">
                    <div className="flex justify-between">
                      <span>Last sync</span>
                      <span>{new Date(it.last_sync_at).toLocaleString()}</span>
                    </div>
                    {it.last_sync_totals && (
                      <div className="flex justify-between mt-1">
                        <span>Records</span>
                        <span className="font-mono">
                          {Object.entries(it.last_sync_totals).map(([k, v]) => `${k.split("_")[0]} ${v}`).join(" · ")}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-auto pt-2">
                  {!connected ? (
                    <button
                      onClick={() => openConnect(it)}
                      data-testid={`mc-integration-connect-${it.provider}`}
                      className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground"
                    >
                      Connect
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => sync(it)}
                        disabled={syncing === it.provider}
                        data-testid={`mc-integration-sync-${it.provider}`}
                        className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground inline-flex items-center gap-1.5 disabled:opacity-60"
                      >
                        <RefreshCw size={12} className={syncing === it.provider ? "animate-spin" : ""} />
                        {syncing === it.provider ? "Syncing…" : "Sync now"}
                      </button>
                      <button onClick={() => openHistory(it)} className="text-xs px-3 py-1.5 rounded border border-border">
                        History
                      </button>
                      <button onClick={() => disconnect(it)} className="text-xs px-3 py-1.5 rounded text-rose-400 hover:bg-rose-500/10 ml-auto">
                        Disconnect
                      </button>
                    </>
                  )}
                  <a href={it.docs_url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:text-foreground ml-auto">
                    docs ↗
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {activeProvider && activeMeta && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActiveProvider(null)}>
          <div className="bg-card border border-border rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mc-integration-modal">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-medium">Connect {activeMeta.name}</h3>
              <button onClick={() => setActiveProvider(null)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-muted-foreground">
                Paste credentials from your {activeMeta.vendor} developer console. They are
                stored encrypted and never returned in plaintext.
              </p>
              {activeMeta.required_fields.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground">{f.label}</label>
                  <input
                    type={f.secret ? "password" : "text"}
                    value={credForm[f.key] || ""}
                    onChange={(e) => setCredForm({ ...credForm, [f.key]: e.target.value })}
                    data-testid={`mc-integration-field-${f.key}`}
                    className="mt-1 w-full px-3 py-2 rounded border border-border bg-background text-sm"
                  />
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setActiveProvider(null)} className="text-sm px-3 py-2 rounded border border-border">Cancel</button>
                <button
                  onClick={submitConnect}
                  data-testid="mc-integration-submit"
                  className="text-sm px-3 py-2 rounded bg-primary text-primary-foreground"
                >
                  Save & connect
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setHistoryOpen(null)}>
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mc-integration-history">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-medium">Sync history — {items.find((i) => i.provider === historyOpen)?.name}</h3>
              <button onClick={() => setHistoryOpen(null)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="p-5">
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No sync runs yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="text-left py-2">Started</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Triggered by</th>
                      <th className="text-right py-2">Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} className="border-b border-border/40">
                        <td className="py-2 text-xs">{new Date(h.started_at).toLocaleString()}</td>
                        <td className="py-2"><span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 uppercase">{h.status}</span></td>
                        <td className="py-2 text-xs text-muted-foreground">{h.triggered_by_email}</td>
                        <td className="py-2 text-right text-xs font-mono">{Object.values(h.totals).reduce((a, b) => a + b, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== Help Desk — Support Ticketing Inbox ==============
function HelpDeskTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("open");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", organization_id: "", severity: "medium" });

  const load = async () => {
    const r = await api.get("/api/mission-control/cases", { params: { kind: "support" } });
    setRows(r.data);
    if (active) {
      const fresh = r.data.find((c) => c.id === active.id);
      if (fresh) setActive(fresh);
    }
  };
  useEffect(() => { load(); mcListOrgs({}).then((r) => setOrgs(r.orgs)); }, []); // eslint-disable-line

  const create = async () => {
    try {
      const r = await api.post("/api/mission-control/cases", { ...form, kind: "support", organization_id: form.organization_id || null });
      toast.success("Ticket opened");
      setOpenNew(false); setForm({ title: "", description: "", organization_id: "", severity: "medium" });
      await load();
      setActive(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async (patch) => {
    if (!active) return;
    try {
      const r = await api.patch(`/api/mission-control/cases/${active.id}`, patch);
      setActive(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const postReply = async () => {
    if (!reply.trim() || !active) return;
    setPosting(true);
    try {
      await update({ note: reply.trim() });
      setReply("");
    } finally { setPosting(false); }
  };

  const claim = () => update({ assignee_email: user?.email });

  const counts = {
    all: rows.length,
    open: rows.filter((r) => r.status === "open").length,
    investigating: rows.filter((r) => r.status === "investigating").length,
    resolved: rows.filter((r) => ["resolved", "closed"].includes(r.status)).length,
    overdue: rows.filter((r) => {
      const s = slaInfo(r);
      return s && s.overdue && !s.isResolved;
    }).length,
  };

  const filtered = rows.filter((r) => {
    if (filter === "open" && r.status !== "open") return false;
    if (filter === "investigating" && r.status !== "investigating") return false;
    if (filter === "resolved" && !["resolved", "closed"].includes(r.status)) return false;
    if (filter === "overdue") {
      const s = slaInfo(r);
      if (!s || !s.overdue || s.isResolved) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!r.title?.toLowerCase().includes(q) && !r.description?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const orgName = (id) => orgs.find((o) => o.id === id)?.name || "—";
  const activeSla = active ? slaInfo(active) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium serif">Help Desk</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Unified inbox for tenant support tickets. SLA timers fire by severity (critical 4h · high 24h · medium 72h · low 7d).</p>
        </div>
        <button onClick={() => setOpenNew(true)} data-testid="mc-helpdesk-new" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
          <Plus size={14} /> New ticket
        </button>
      </div>

      <div className="grid grid-cols-5 gap-3">
        {[
          { id: "open", label: "Open", v: counts.open, tone: "sky" },
          { id: "investigating", label: "In progress", v: counts.investigating, tone: "amber" },
          { id: "overdue", label: "SLA overdue", v: counts.overdue, tone: "rose" },
          { id: "resolved", label: "Resolved", v: counts.resolved, tone: "emerald" },
          { id: "all", label: "All tickets", v: counts.all, tone: "muted" },
        ].map((c) => (
          <button
            key={c.id}
            onClick={() => setFilter(c.id)}
            data-testid={`mc-helpdesk-filter-${c.id}`}
            className={`text-left rounded-xl border p-3 transition-colors ${
              filter === c.id ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-accent/40"
            }`}
          >
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
            <div className={`mt-1 text-2xl serif ${
              c.tone === "rose" ? "text-rose-400" :
              c.tone === "amber" ? "text-amber-400" :
              c.tone === "emerald" ? "text-emerald-400" :
              c.tone === "sky" ? "text-sky-400" :
              "text-foreground"
            }`}>{c.v}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Ticket list */}
        <div className="lg:col-span-1 rounded-xl border border-border bg-card flex flex-col min-h-[520px] max-h-[640px]">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tickets…"
                data-testid="mc-helpdesk-search"
                className="w-full text-sm pl-8 pr-3 py-1.5 rounded border border-border bg-background"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">No tickets match.</div>
            ) : filtered.map((c) => {
              const sla = slaInfo(c);
              const isActive = active?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c)}
                  data-testid={`mc-helpdesk-ticket-${c.id}`}
                  className={`w-full text-left border-b border-border/40 px-3 py-3 transition-colors ${
                    isActive ? "bg-primary/10" : "hover:bg-accent/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm line-clamp-1">{c.title}</div>
                    {sla && !sla.isResolved && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        sla.overdue ? "bg-rose-500/20 text-rose-400" : "bg-sky-500/15 text-sky-400"
                      }`}>{sla.overdue ? `+${sla.label}` : sla.label}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded uppercase ${
                      c.severity === "critical" ? "bg-rose-500/20 text-rose-400" :
                      c.severity === "high" ? "bg-amber-500/20 text-amber-400" :
                      "bg-accent text-muted-foreground"
                    }`}>{c.severity}</span>
                    <span>{c.status}</span>
                    {c.assignee_email && <span className="ml-auto truncate max-w-[120px]">@ {c.assignee_email.split("@")[0]}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ticket detail */}
        <div className="lg:col-span-2 rounded-xl border border-border bg-card flex flex-col min-h-[520px] max-h-[640px]" data-testid="mc-helpdesk-detail">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Select a ticket on the left to view the conversation.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-border">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-medium text-base truncate">{active.title}</h3>
                    <div className="text-xs text-muted-foreground mt-1">
                      {orgName(active.organization_id)} · opened by {active.opened_by_email} · {new Date(active.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeSla && (
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase ${
                        activeSla.isResolved ? "bg-emerald-500/15 text-emerald-400" :
                        activeSla.overdue ? "bg-rose-500/20 text-rose-400" :
                        "bg-sky-500/15 text-sky-400"
                      }`} title={`SLA: ${active.sla_hours}h`}>
                        {activeSla.isResolved ? "SLA met" : activeSla.overdue ? `SLA +${activeSla.label} late` : `SLA ${activeSla.label}`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <select
                    value={active.status}
                    onChange={(e) => update({ status: e.target.value })}
                    data-testid="mc-helpdesk-status"
                    className="text-xs bg-background border border-border rounded px-2 py-1"
                  >
                    {["open", "investigating", "resolved", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select
                    value={active.severity}
                    onChange={(e) => update({ severity: e.target.value })}
                    className="text-xs bg-background border border-border rounded px-2 py-1"
                  >
                    {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {active.assignee_email ? (
                    <span className="text-xs px-2 py-1 rounded bg-accent text-foreground inline-flex items-center gap-1.5">
                      <UserCheck size={12} /> {active.assignee_email}
                      <button onClick={() => update({ assignee_email: "" })} className="ml-1 hover:text-rose-400"><X size={10} /></button>
                    </span>
                  ) : (
                    <button onClick={claim} data-testid="mc-helpdesk-claim" className="text-xs px-2 py-1 rounded border border-border inline-flex items-center gap-1.5 hover:bg-accent/60">
                      <UserPlus size={12} /> Claim
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {active.description && (
                  <div className="rounded-lg border border-border/60 bg-background/40 p-3">
                    <div className="text-[11px] text-muted-foreground mb-1">{active.opened_by_email} · opened ticket</div>
                    <p className="text-sm whitespace-pre-wrap">{active.description}</p>
                  </div>
                )}
                {(active.events || []).map((ev, i) => (
                  <div key={i} className={`rounded-lg border p-3 ${
                    ev.actor_email === user?.email ? "border-primary/40 bg-primary/5 ml-8" : "border-border/60 bg-background/40 mr-8"
                  }`}>
                    <div className="text-[11px] text-muted-foreground mb-1 flex items-center justify-between">
                      <span>{ev.actor_email}</span>
                      <span>{new Date(ev.at).toLocaleString()}</span>
                    </div>
                    {ev.note ? (
                      <p className="text-sm whitespace-pre-wrap">{ev.note}</p>
                    ) : (
                      <p className="text-xs italic text-muted-foreground">{ev.action || "event"}{ev.doc_type ? ` · ${ev.doc_type}` : ""}</p>
                    )}
                  </div>
                ))}
                {(!active.events || active.events.length === 0) && (
                  <p className="text-xs text-muted-foreground text-center py-6">No replies yet.</p>
                )}
              </div>

              <div className="p-3 border-t border-border bg-background/40">
                <div className="flex items-end gap-2">
                  <textarea
                    rows={2}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Reply to ticket…"
                    data-testid="mc-helpdesk-reply"
                    className="flex-1 text-sm px-3 py-2 rounded border border-border bg-background resize-none"
                  />
                  <button
                    onClick={postReply}
                    disabled={!reply.trim() || posting}
                    data-testid="mc-helpdesk-send"
                    className="text-sm px-3 py-2 rounded bg-primary text-primary-foreground inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    <Send size={14} /> {posting ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {openNew && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenNew(false)}>
          <div className="bg-card border border-border rounded-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="mc-helpdesk-new-modal">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-medium">New support ticket</h3>
              <button onClick={() => setOpenNew(false)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded border border-border bg-background text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} className="px-3 py-2 rounded border border-border bg-background text-sm">
                  <option value="">-- Org (optional) --</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="px-3 py-2 rounded border border-border bg-background text-sm">
                  {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <textarea rows={4} placeholder="Describe the issue…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded border border-border bg-background text-sm" />
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setOpenNew(false)} className="text-sm px-3 py-2 rounded border border-border">Cancel</button>
                <button onClick={create} disabled={!form.title} data-testid="mc-helpdesk-create" className="text-sm px-3 py-2 rounded bg-primary text-primary-foreground">Open ticket</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== Shell ==============
const TAB_COMPONENTS = {
  overview: () => (<div className="space-y-6"><OverviewTab /><AlertsCenter /></div>),
  orgs: OrgsTab,
  verification: () => <CasesTab kind="verification" title="Verification" subtitle="Document checks (EIN, 501(c)(3), bank verification). Open a case when an org's docs need review." severityDefault="low" />,
  trust_safety: () => <CasesTab kind="trust_safety" title="Trust & Safety" subtitle="Investigations into account abuse, campaign freezes, suspensions." severityDefault="medium" />,
  fraud: () => (<div className="space-y-6"><FraudTab /><FraudRulesTab /></div>),
  support: HelpDeskTab,
  ops: OpsTab,
  compliance: ComplianceTab,
  impact: ImpactTab,
  users: PlatformUsersTab, pricing: PricingTab, promos: PromosTab,
  stripe: StripeTab, mfa: MfaTab, bulk: BulkUploadTab, audit: AuditTab, system: SystemTab,
  integrations: IntegrationsTab,
};

export default function MissionControl() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const handleSignOut = async () => {
    try { await signOut(); } catch (e) { /* no-op */ }
    navigate("/login", { replace: true });
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!PLATFORM_ROLES.includes(user.role)) {
    return (
      <div className="p-8">
        <div className="max-w-md rounded-xl border border-border p-6 bg-card">
          <h2 className="font-medium text-lg">Mission Control is for platform admins</h2>
          <p className="text-sm text-muted-foreground mt-2">
            You&apos;re signed in as <strong>{user.role}</strong>. Only Super, Regional and Financials Admins can access this surface.
          </p>
        </div>
      </div>
    );
  }

  const Active = TAB_COMPONENTS[tab] || OverviewTab;

  const isAllowed = (id) => {
    if (user.role === "super_admin") return true;
    if (["overview", "orgs", "audit"].includes(id)) return true;
    if (user.role === "financials_admin") return ["overview", "orgs", "pricing", "promos", "stripe", "audit", "impact", "integrations"].includes(id);
    if (user.role === "regional_admin") return ["overview", "orgs", "mfa", "audit", "bulk", "verification", "trust_safety", "fraud", "support", "ops", "compliance", "impact"].includes(id);
    return false;
  };

  const greetingName = (user.full_name || user.email || "").split(/[ @]/)[0] || "Admin";
  const greetingDisplay = greetingName.length <= 2
    ? (ROLE_LABEL[user.role] || "Admin")
    : greetingName.charAt(0).toUpperCase() + greetingName.slice(1);
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="dark bg-background text-foreground min-h-screen flex flex-col">
      <header className="border-b border-border bg-card/40 backdrop-blur sticky top-0 z-30" data-testid="mc-header">
        <div className="max-w-7xl mx-auto px-8 py-3 flex items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Logo height={28} />
            <div className="hidden md:block h-7 w-px bg-border" />
            <div className="min-w-0">
              <h1 className="serif text-xl font-medium text-primary leading-tight">Mission Control</h1>
              <p className="text-[11px] text-muted-foreground leading-tight">
                {ROLE_LABEL[user.role] || user.role}{user.region ? ` · ${user.region}` : ""}
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full bg-accent/60 text-muted-foreground font-mono" data-testid="mc-version">
              {MC_VERSION}
            </span>
            <span className="hidden lg:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/15 text-primary">
              <Activity size={12} /> platform
            </span>
            <div className="hidden sm:flex flex-col items-end leading-tight" data-testid="mc-greeting">
              <span className="text-xs text-muted-foreground">{greet},</span>
              <span className="text-sm font-medium">{greetingDisplay}</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary text-xs font-medium flex items-center justify-center" title={user.email}>
              {greetingDisplay.slice(0, 2).toUpperCase()}
            </div>
            <button
              onClick={handleSignOut}
              data-testid="mc-signout"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:bg-rose-500/10 hover:text-rose-400 hover:border-rose-500/30 transition-colors"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="flex-1 max-w-7xl w-full mx-auto px-8 py-6">
        <div className="flex gap-8">
          <aside className="w-60 shrink-0 space-y-6" data-testid="mc-nav">
            {NAV_GROUPS.map((group) => {
              const visible = group.items.filter((it) => isAllowed(it.id));
              if (visible.length === 0) return null;
              return (
                <div key={group.label}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-medium px-3 mb-2">
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {visible.map((t) => {
                      const I = t.icon;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setTab(t.id)}
                          data-testid={`mc-tab-${t.id}`}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                            tab === t.id
                              ? "bg-primary/15 text-primary font-medium"
                              : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                          }`}
                        >
                          <I size={14} strokeWidth={1.75} /> {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="px-3 pt-4 mt-4 border-t border-border/40 text-[10px] text-muted-foreground/70 leading-relaxed">
              Powered by <a href="https://www.itprofound.com/" target="_blank" rel="noreferrer" className="hover:text-primary underline-offset-2 hover:underline">ITProFound</a>
              <div className="mt-1 font-mono">TrustDime {MC_VERSION}</div>
            </div>
          </aside>
          <div className="flex-1 min-w-0" data-testid="mc-content">
            <Active />
          </div>
        </div>
      </div>
    </div>
  );
}
